const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

const recent = new Map<string, number>();

export function makeErrorCode() {
  return "ERR-" + Math.random().toString(36).slice(2, 8).toUpperCase();
}

/** Logs an error to the backend and emails the admin. Returns a short code to show the user. */
export function reportError(message: string, context?: string, details?: Record<string, unknown>): string {
  const code = makeErrorCode();
  const msg = String(message || "Unknown error").slice(0, 2000);
  const now = Date.now();
  if ((recent.get(msg) ?? 0) > now - 30_000) return code; // de-dupe bursts
  recent.set(msg, now);
  try {
    fetch(`${SUPABASE_URL}/functions/v1/report-error`, {
      method: "POST",
      keepalive: true,
      headers: { "Content-Type": "application/json", apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}` },
      body: JSON.stringify({
        code,
        message: msg,
        context,
        page: window.location.href,
        userAgent: navigator.userAgent,
        details: { ...details, online: navigator.onLine, time: new Date().toISOString() },
      }),
    }).catch(() => {});
  } catch {
    /* never throw from the reporter */
  }
  return code;
}

let installed = false;
export function installGlobalErrorReporting() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  const isIgnored = (msg: string, stack?: string, source?: string) => {
    if (!msg || !msg.trim()) return true;
    const lower = msg.toLowerCase().trim();
    const str = `${msg} ${stack || ""} ${source || ""}`.toLowerCase();

    // 1. Browser extensions
    if (str.includes("chrome-extension:") || str.includes("moz-extension:") || str.includes("safari-extension:")) return true;

    // 2. Generic script errors & empty promise rejections
    if (msg === "Script error." || lower === "rejected" || lower === "uncaught (in promise)") return true;

    // 3. User aborts & cancellations
    if (lower.includes("abort") || lower.includes("canceled") || lower.includes("cancelled")) return true;

    // 4. ServiceWorker & PWA registration failures (SW script load errors, 404s, scope errors)
    if (
      lower.includes("sw.js") ||
      lower.includes("serviceworker") ||
      lower.includes("service worker") ||
      lower.includes("register-sw") ||
      lower.includes("an unknown error occurred when fetching the script")
    ) {
      return true;
    }

    // 5. Dynamic module import failures (handled gracefully by lazyWithRetry page reload)
    if (
      lower.includes("failed to fetch dynamically imported module") ||
      lower.includes("importing a module script failed") ||
      lower.includes("chunkloaderror")
    ) {
      return true;
    }

    // 6. User authentication / login validation inputs (invalid password, bad auth credentials)
    if (
      lower.includes("invalid email or password") ||
      lower.includes("login failed") ||
      lower.includes("failed to load users")
    ) {
      return true;
    }

    // 7. Offline network failures
    if (!navigator.onLine && (lower.includes("failed to fetch") || lower.includes("networkerror") || lower.includes("loading chunk"))) {
      return true;
    }

    return false;
  };

  window.addEventListener("error", (e) => {
    const stack = e.error?.stack?.slice(0, 2000);
    if (isIgnored(e.message, stack, e.filename)) return;
    reportError(e.message, "page-crash", { source: e.filename, line: e.lineno, stack });
  });

  window.addEventListener("unhandledrejection", (e) => {
    const r = e.reason;
    const msg = r instanceof Error ? r.message : typeof r === "string" ? r : "";
    const stack = r?.stack?.slice(0, 2000);
    if (isIgnored(msg, stack)) return;
    reportError(msg, "unhandled", { stack });
  });
}
